import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

export interface DebugDialogData {
    title: string;
    messages: string[];
    startTime: number;
    endTime: number;
}

/**
 * Debug Dialog Component
 *
 * Displays collected debug messages in a textarea for easy copy/paste.
 * Shows statistics like message count and elapsed time.
 */
@Component({
    selector: 'app-debug-dialog',
    templateUrl: './debug-dialog.component.html',
    styleUrls: ['./debug-dialog.component.scss'],
    standalone: false
})
export class DebugDialogComponent implements OnInit {
    debugText = '';
    messageCount = 0;
    elapsedTime = '';
    charCount = 0;
    lineCount = 0;

    constructor(
        public dialogRef: MatDialogRef<DebugDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DebugDialogData,
        private toastr: ToastrService
    ) { }

    ngOnInit(): void {
        // Join all messages into single text block
        this.debugText = this.data.messages.join('\n');
        this.messageCount = this.data.messages.length;
        this.charCount = this.debugText.length;
        this.lineCount = this.data.messages.length;

        // Calculate elapsed time
        const elapsed = this.data.endTime - this.data.startTime;
        const seconds = (elapsed / 1000).toFixed(2);
        this.elapsedTime = `${seconds}s`;
    }

    /**
     * Copy all debug text to clipboard
     */
    copyToClipboard(): void {
        const textarea = document.getElementById('debugTextarea') as HTMLTextAreaElement;
        if (textarea) {
            textarea.select();
            document.execCommand('copy');

            this.toastr.success(
                `Copied ${this.charCount} characters to clipboard`,
                'Debug Logs Copied',
                { timeOut: 2000 }
            );
        }
    }

    /**
     * Download debug logs as text file
     */
    downloadLogs(): void {
        const blob = new Blob([this.debugText], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `debug-${this.data.title.replace(/\s+/g, '-')}-${Date.now()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        this.toastr.success(
            'Debug logs downloaded',
            'File Saved',
            { timeOut: 2000 }
        );
    }

    /**
     * Clear textarea (doesn't affect service data)
     */
    clearText(): void {
        this.debugText = '';
        this.charCount = 0;
    }

    /**
     * Close dialog
     */
    close(): void {
        this.dialogRef.close();
    }
}
